import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';

const ITALY = [[36.4, 6.6], [47.1, 18.6]];

// OSM tiles: free and keyless (CARTO now wants an API key). Dark mode inverts
// them with a CSS filter, see .lh-map in index.css.
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTRIBUTION = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const pinIcon = (active) => L.divIcon({
  className: '',
  html: `<div class="lh-pin${active ? ' lh-pin--active' : ''}"><div class="lh-pin__head"></div></div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 34],
});

const clusterIcon = (cluster) => L.divIcon({
  className: '',
  html: `<div class="lh-cluster">${cluster.getChildCount()}</div>`,
  iconSize: [40, 40],
});

const hasCoords = (f) => f.lat != null && f.lng != null;

function useLeafletMap(containerRef) {
  const mapRef = useRef(null);

  useEffect(() => {
    const map = L.map(containerRef.current, { attributionControl: false, zoomSnap: 0.5 });
    L.control.attribution({ prefix: false }).addTo(map);
    L.tileLayer(TILE_URL, { attribution: ATTRIBUTION, maxZoom: 19 }).addTo(map);
    map.fitBounds(ITALY);
    // The map may mount hidden (mobile list/map toggle): resize once it shows.
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(containerRef.current);
    mapRef.current = map;
    return () => {
      observer.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, [containerRef]);

  return mapRef;
}

/**
 * Marketplace map: one pin per facility, grouped into counters when they overlap.
 * Opens on the whole of Italy; `fitToPins` zooms on the results instead (filters, detail page).
 */
export const FacilitiesMap = ({ facilities, selectedId, onSelect, fitToPins = false, className = '' }) => {
  const containerRef = useRef(null);
  const mapRef = useLeafletMap(containerRef);
  const markersRef = useRef({});
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const located = facilities.filter(hasCoords);
  const locatedKey = located.map((f) => f.facility_id).join(',');

  useEffect(() => {
    const map = mapRef.current;
    const cluster = L.markerClusterGroup({
      iconCreateFunction: clusterIcon,
      showCoverageOnHover: false,
      maxClusterRadius: 50,
    });
    markersRef.current = {};
    located.forEach((f) => {
      const marker = L.marker([f.lat, f.lng], { icon: pinIcon(false), title: f.name });
      marker.on('click', () => onSelectRef.current?.(f.facility_id));
      markersRef.current[f.facility_id] = marker;
      cluster.addLayer(marker);
    });
    map.addLayer(cluster);
    if (fitToPins && located.length) {
      const bounds = L.latLngBounds(located.map((f) => [f.lat, f.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: located.length === 1 ? 15 : 13 });
    } else {
      map.fitBounds(ITALY);
    }
    return () => map.removeLayer(cluster);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locatedKey]);

  useEffect(() => {
    Object.entries(markersRef.current).forEach(([id, m]) => m.setIcon(pinIcon(id === selectedId)));
  }, [selectedId, locatedKey]);

  return <div ref={containerRef} className={`lh-map ${className}`} data-testid="facilities-map" />;
};

/** Owner-side picker: click the map or drag the pin to place the facility. */
export const LocationPicker = ({ lat, lng, onChange, className = '' }) => {
  const containerRef = useRef(null);
  const mapRef = useLeafletMap(containerRef);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const map = mapRef.current;
    const marker = L.marker([0, 0], { icon: pinIcon(true), draggable: true });
    marker.on('dragend', () => onChangeRef.current(marker.getLatLng()));
    map.on('click', (e) => onChangeRef.current(e.latlng));
    markerRef.current = marker;
    return () => {
      map.off('click');
      marker.remove();
    };
  }, [mapRef]);

  useEffect(() => {
    if (lat == null || lng == null) return;
    const map = mapRef.current;
    const point = L.latLng(lat, lng);
    markerRef.current.setLatLng(point).addTo(map);
    // Searching or clicking from the whole of Italy zooms straight to the street.
    if (map.getZoom() < 14) map.setView(point, 16);
    else if (!map.getBounds().contains(point)) map.panTo(point);
  }, [lat, lng, mapRef]);

  return <div ref={containerRef} className={`lh-map ${className}`} data-testid="location-picker" />;
};

export default FacilitiesMap;
